import { HgsArticle, InspectionItemInfo } from 'api3';
import { CommonModule } from '@angular/common';
import { Component, computed, input } from '@angular/core';
import { formatDuration } from '@app/shared/dateHelper';
import { HgsInspectionWireStatusComponent } from './components/hgs-inspection-wirestatus.component';
import { splitServiceTitle } from './hgs-helper';
import { TranslocoPipe } from '@jsverse/transloco';

@Component({
  selector: 'app-hgs-inspection',
  imports: [CommonModule, TranslocoPipe, HgsInspectionWireStatusComponent],
  styles: [
    `
      .ok-radio,
      .not-ok-radio {
        min-width: 60px;
      }
      tr:has(.ok-radio input:checked) .not-ok-radio label,
      tr:has(.not-ok-radio input:checked) .ok-radio label {
        opacity: 0.2;
      }
    `,
  ],
  template: `
    <h2>
      <strong class="block">{{ titleAndSubtitle()?.name }}</strong>
      <span>{{ titleAndSubtitle()?.interval }}</span>
    </h2>

    <div class="mt-4 max-w-[1400px]">
      <ol class="space-y-6">
        @for (inspection of data()?.data?.inspectionDocument?.inspections || []; track inspection.name; let inspIdx = $index) {
          <li>
            <table class="w-full table-fixed border border-gray-300">
              <colgroup>
                <col class="w-[20px] md:w-8" />
                <col />
                <col class="w-10 md:w-16 xl:w-24" />
                <col class="w-10 md:w-16 xl:w-24" />
                <col class="w-[30%]" />
              </colgroup>
              <thead>
                <tr class="border-b border-gray-300 bg-gray-100 text-xs text-slate-500 md:text-sm">
                  <th colspan="2" class="p-2 text-left">{{ inspection.name }}</th>
                  <th class="p-2">{{ 'documents.hgs.inspection.ok' | transloco }}</th>
                  <th class="p-2">{{ 'documents.hgs.inspection.notOk' | transloco }}</th>
                  <th class="truncate p-2 text-left">{{ 'documents.hgs.inspection.comment' | transloco }}</th>
                </tr>
              </thead>
              <tbody>
                @for (task of inspection.items; track task.name; let taskIdx = $index) {
                  <tr class="align-top text-xs md:text-sm">
                    <td class="p-2 text-center max-sm:pl-1">{{ getGlobalIndex(inspIdx, taskIdx) }}</td>
                    <td class="overflow-hidden p-2 max-sm:pl-1">
                      <p>
                        <span class="mr-1 uppercase">{{ task.operation }}:</span>
                        <strong class="mr-2 font-normal">{{ task.name }}</strong>
                        <span class="-ml-1 inline-block whitespace-nowrap rounded-xl bg-sky-100 px-2 py-[1px] text-sky-800">
                          {{ 'documents.hgs.inspection.needs' | transloco: { time: getDurationString(task) } }}
                        </span>
                      </p>
                    </td>
                    <td class="ok-radio relative text-center">
                      <label
                        class="absolute inset-x-1 top-2 flex h-12 items-center justify-center overflow-hidden rounded-lg has-[input:checked]:bg-green-100">
                        <input
                          type="radio"
                          [name]="'inspection-' + inspIdx + taskIdx"
                          [value]="taskIdx + 'ok'"
                          class="peer !h-4 !w-4 opacity-50 checked:invisible md:!h-6 md:!w-6" />
                        <i class="pi pi-check invisible absolute inset-auto !text-xl text-green-700 peer-checked:!visible xl:!text-2xl"></i>
                      </label>
                    </td>
                    <td class="not-ok-radio relative text-center">
                      <label
                        class="absolute inset-x-1 top-2 flex h-12 items-center justify-center overflow-hidden rounded-lg has-[input:checked]:bg-red-100">
                        <input
                          type="radio"
                          [name]="'inspection-' + inspIdx + taskIdx"
                          [value]="taskIdx + 'notok'"
                          class="peer !h-4 !w-4 opacity-50 checked:invisible md:!h-6 md:!w-6" />
                        <i class="pi pi-times invisible absolute inset-auto !text-xl text-red-500 peer-checked:!visible xl:!text-2xl"></i>
                      </label>
                    </td>
                    <td class="px-1 pt-2 md:px-2">
                      <textarea rows="2" class="min-h-12 w-full resize-none rounded-lg bg-gray-50 p-1 text-xs md:p-2 md:text-sm"></textarea>
                    </td>
                  </tr>
                }
              </tbody>
            </table>
          </li>
        }
      </ol>
    </div>

    <div class="mt-8 max-w-[1400px] print:break-inside-avoid">
      <h3 class="text-base font-semibold">{{ 'documents.hgs.inspection.tires.title' | transloco }}</h3>
      <div class="mt-4 flex justify-center border border-slate-300 pl-4">
        <div class="flex flex-1 items-stretch justify-start">
          <div class="relative flex-1 py-24">
            <div class="absolute left-0 top-2 sm:left-12 md:left-24"><app-hgs-inspection-wirestatus /></div>
            <div class="absolute right-0 top-2 sm:right-6 md:right-24 xl:right-32"><app-hgs-inspection-wirestatus /></div>
            <img src="assets/images/hgs/orig_car_template.png" class="h-auto w-full" alt="" />
            <div class="absolute bottom-2 left-0 sm:left-12 md:left-24"><app-hgs-inspection-wirestatus /></div>
            <div class="absolute bottom-2 right-0 sm:right-6 md:right-24 xl:right-32"><app-hgs-inspection-wirestatus /></div>
          </div>
          <div class="flex items-center justify-start px-2 sm:px-4"><app-hgs-inspection-wirestatus /></div>
        </div>
      </div>

      <div class="mt-6 flex max-w-[1400px] gap-4">
        <div class="w-1/2">
          <label class="w-full">
            <span class="block pb-1 pt-2 font-semibold">{{ 'documents.hgs.inspection.tires.HU' | transloco }}</span>
            <input type="date" class="w-full border border-slate-300 px-2 py-1 text-base" />
          </label>
        </div>
        <div class="w-1/2">
          <label class="w-full">
            <span class="block pb-1 pt-2 font-semibold">{{ 'documents.hgs.inspection.tires.FirstAidBox' | transloco }}</span>
            <input type="date" class="w-full border border-slate-300 px-2 py-1 text-base" />
          </label>
        </div>
      </div>

      <div class="mt-4 max-w-[1400px]">
        <label class="block w-full">
          <span class="block pb-1 pt-2 font-semibold">{{ 'documents.hgs.inspection.comment' | transloco }}</span>
          <textarea [rows]="4" class="w-full border border-slate-300 px-2 py-1 text-base"></textarea>
        </label>
      </div>
    </div>

    <div class="mb-12 mt-8 print:break-inside-avoid">
      <h3 class="text-base font-semibold">{{ 'documents.hgs.inspection.tools.title' | transloco }}</h3>
      <ol class="mt-2 list-decimal space-y-1 pl-5">
        @for (component of data()?.data?.inspectionDocument?.components || []; track component) {
          <li>{{ component }}</li>
        }
      </ol>
    </div>

    <pre class="mt-4 hidden max-w-full overflow-hidden rounded-lg bg-violet-50">{{ data() | json }}</pre>
  `,
  standalone: true,
})
export class HgsInspectionComponent {
  data = input<HgsArticle | undefined>();

  titleAndSubtitle = computed(() => splitServiceTitle(this.data()?.data?.inspectionDocument?.title));

  getGlobalIndex(inspectionIndex: number, taskIndex: number): number {
    const inspections = this.data()?.data?.inspectionDocument?.inspections || [];
    let globalIndex = 1;

    // Zähle alle Tasks aus vorherigen Inspektionen
    for (let i = 0; i < inspectionIndex; i++) {
      globalIndex += inspections[i]?.items?.length || 0;
    }

    // Addiere den aktuellen Task-Index
    return globalIndex + taskIndex;
  }

  getDurationString(task: InspectionItemInfo) {
    return formatDuration(task.aggregatedLabourTime);
  }
}
